import React, { useCallback, useEffect, useRef, useState } from "react";
import { makeAPI } from "../services/api";
import MyorderCards from "../components/store/my_orders_card";

function getStoredUser() {
    try {
        const raw = localStorage.getItem("user");
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === "object" ? parsed : null;
    } catch (error) {
        return null;
    }
}

const PAGE_LIMIT = 6;

const MyOrders = () => {
    const [user, setUser] = useState(() => getStoredUser());
    const [orders, setOrders] = useState([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const [initialLoaded, setInitialLoaded] = useState(false);
    const isFetchingRef = useRef(false);
    const lastFetchedPageRef = useRef(0);

    useEffect(() => {
        const syncUser = () => setUser(getStoredUser());
        syncUser();
        window.addEventListener("auth:updated", syncUser);
        window.addEventListener("storage", syncUser);
        return () => {
            window.removeEventListener("auth:updated", syncUser);
            window.removeEventListener("storage", syncUser);
        };
    }, []);

    useEffect(() => {
        document.title = "My Orders - CGES.CLUB";
    }, []);

    useEffect(() => {
        setOrders([]);
        setPage(1);
        setHasMore(true);
        setInitialLoaded(false);
        isFetchingRef.current = false;
        lastFetchedPageRef.current = 0;
    }, [user?.user_id]);

    const fetchOrders = useCallback(async (targetPage) => {
        if (!user?.user_id || !hasMore) return;
        if (isFetchingRef.current) return;
        if (targetPage <= lastFetchedPageRef.current) return;
        isFetchingRef.current = true;
        setLoading(true);
        try {
            const res = await makeAPI("/api/myorder", "POST", false, {
                user_id: Number(user.user_id),
                page: targetPage,
                limit: PAGE_LIMIT
            });

            if (res?.status === "S" && Array.isArray(res?.data)) {
                const incoming = res.data;
                setOrders((prev) => {
                    const next = targetPage === 1 ? incoming : [...prev, ...incoming];
                    return next;
                });

                const totalCount = Number(res?.total_count || 0);
                const loadedCount = targetPage * PAGE_LIMIT;
                setHasMore(loadedCount < totalCount && incoming.length > 0);
                lastFetchedPageRef.current = targetPage;
            } else {
                if (targetPage === 1) setOrders([]);
                setHasMore(false);
            }
        } catch (error) {
            setHasMore(false);
        } finally {
            isFetchingRef.current = false;
            setLoading(false);
            setInitialLoaded(true);
        }
    }, [user?.user_id, hasMore]);

    useEffect(() => {
        if (!user?.user_id) return;
        fetchOrders(page);
    }, [page, user?.user_id, fetchOrders]);

    const handleLoadMore = () => {
        if (!hasMore || loading || isFetchingRef.current) return;
        setPage((prev) => prev + 1);
    };

    const openLoginModal = (e) => {
        if (e) e.preventDefault();
        if (window.UIkit && typeof window.UIkit.modal === "function") {
            window.UIkit.modal("#modal-login", { container: false }).show();
            return;
        }
        window.location.href = "/";
    };

    if (!user || !user?.user_id) {
        return (
            <>
                <div className="container pd-top-120 pd-bottom-90">
                    <div className="row justify-content-center">
                        <div className="col-lg-8">
                            <div className="orders-brochure-hero text-center">
                                <h2 className="title move-line-3d mb-2">My Orders</h2>
                                <p className="orders-brochure-subtitle">
                                    Login to view your order history, order status, and shipment details in one place.
                                </p>
                                <button className="btn btn-base orders-brochure-cta" onClick={openLoginModal}>
                                    Login To Continue
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                <style>{`
                    .orders-brochure-hero {
                        background: radial-gradient(circle at top right, rgba(255, 129, 14, 0.26), transparent 42%), #121212;
                        border: 1px solid #3a3a3a;
                        border-radius: 14px;
                        padding: 26px 20px;
                    }
                    .orders-brochure-subtitle {
                        max-width: 720px;
                        margin: 0 auto 14px;
                        color: #e4e4e4;
                    }
                    .orders-brochure-cta {
                        border: 1px solid #ff810e;
                        background: #161616;
                        color: #fff;
                        min-width: 220px;
                    }
                    @media (max-width: 767px) {
                        .orders-brochure-hero {
                            padding: 20px 14px;
                        }
                        .orders-brochure-cta {
                            width: 100%;
                            min-width: 0;
                        }
                    }
                `}</style>
            </>
        );
    }

    return (
        <>
            <MyorderCards couponData={orders} loading={loading && orders.length === 0} />

            {initialLoaded && orders.length > 0 ? (
                <div className="container-md pb-5">
                    <div className="text-center mt-3" style={{ color: "#c8c8c8" }}>
                        {loading ? (
                            "Loading more orders..."
                        ) : hasMore ? (
                            <a
                                href="#!"
                                onClick={(event) => {
                                    event.preventDefault();
                                    handleLoadMore();
                                }}
                                style={{ color: "#ffb165", textDecoration: "underline" }}
                            >
                                Load more
                            </a>
                        ) : ""}
                    </div>
                </div>
            ) : null}
        </>
    );
};

export default MyOrders;
